import styled from '@emotion/styled'
import React from 'react'
import { Hugeicon } from '../../../icons/Hugeicon'

export type OrganizerType = 'myself' | 'organization' | 'other'

type Props = {
  value: OrganizerType
  onChange: (value: OrganizerType) => void
  hasOrganizations: boolean
  showPersonalWarning?: boolean
}

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`

const CardButton = styled.button<{
  $selected: boolean
  $disabled: boolean
}>`
  padding: 12px 10px;
  border: 2px solid ${(props) => (props.$selected ? '#3B82F6' : '#d1d5db')};
  border-radius: 8px;
  text-align: center;
  transition: border-color 0.2s ease;
  background: #fff;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  min-width: 0;

  &:hover:not(:disabled) {
    border-color: ${(props) => (props.$selected ? '#3B82F6' : '#9ca3af')};
  }
`

const IconWrapper = styled.div<{ $selected: boolean }>`
  width: 24px;
  height: 24px;
  margin: 0 auto 6px;
  color: ${(props) => (props.$selected ? '#3B82F6' : '#9ca3af')};
`

const Label = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  margin-bottom: 4px;
  word-wrap: break-word;
  overflow-wrap: break-word;
`

const Description = styled.div`
  font-size: 12px;
  color: #4b5563;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.4;
`

const UserIcon = () => <Hugeicon name="user" size={24} />

const BuildingIcon = () => <Hugeicon name="building" size={24} />

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.4;
  color: #92400e;
  margin-top: -4px;
  margin-bottom: 12px;
`

const OrganizerTypeSelector: React.FC<Props> = ({ value, onChange, hasOrganizations, showPersonalWarning }) => {
  return (
    <>
    <Container>
      <CardButton
        type="button"
        $selected={value === 'myself'}
        $disabled={false}
        onClick={() => onChange('myself')}
      >
        <IconWrapper $selected={value === 'myself'}>
          <UserIcon />
        </IconWrapper>
        <Label>Myself</Label>
        <Description>I&apos;m organizing this event personally</Description>
      </CardButton>

      <CardButton
        type="button"
        $selected={value === 'organization'}
        $disabled={!hasOrganizations}
        disabled={!hasOrganizations}
        onClick={() => hasOrganizations && onChange('organization')}
        title={!hasOrganizations ? 'You must be a member of an organization to select this option' : ''}
      >
        <IconWrapper $selected={value === 'organization'}>
          <BuildingIcon />
        </IconWrapper>
        <Label>An Organization</Label>
        <Description>
          {hasOrganizations ? 'Submitting on behalf of an organization' : 'No organizations available'}
        </Description>
      </CardButton>

      <CardButton
        type="button"
        $selected={value === 'other'}
        $disabled={false}
        onClick={() => onChange('other')}
      >
        <IconWrapper $selected={value === 'other'}>
          <UserIcon />
        </IconWrapper>
        <Label>Other</Label>
        <Description>Submitting for someone else</Description>
      </CardButton>
    </Container>
    {showPersonalWarning && value === 'myself' && (
      <WarningBanner>
        Events submitted personally won&apos;t appear on your business calendar. Select &quot;An Organization&quot; to add it to your calendar.
      </WarningBanner>
    )}
    </>
  )
}

export default OrganizerTypeSelector
